/**
 * Operations dashboard (Phase 7). Live KPIs, pipeline funnel, disbursement-over-time
 * line, category-split donut, recent-activity feed and a background-jobs health widget —
 * all from /admin/analytics + /admin/jobs (recharts). Loading / empty / error states.
 */
import { useEffect, useState } from 'react';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, LineChart, Line, PieChart, Pie, Cell, CartesianGrid,
} from 'recharts';
import { FolderKanban, Wallet, HeartHandshake, Users, RefreshCw, Activity, Cpu } from 'lucide-react';
import { api } from '../lib/apiClient.js';
import { useAuth } from '../auth/AuthProvider.jsx';
import { StatCard } from '../components/ui/StatCard.jsx';
import { Card, CardHeader } from '../components/ui/Card.jsx';
import { formatINR, formatDate } from '../features/cases/caseMeta.js';
import styles from './Dashboard.module.css';

const CAT_COLORS = { housing: '#1B4332', ration: '#BF9447', medical: '#9B3D2E', other: '#2E5A6B' };
const STATUS_ORDER = ['intake', 'field_verify', 'pending_approval', 'approved', 'disbursed', 'delivered', 'closed', 'rejected'];

export default function Dashboard() {
  const { hasRole } = useAuth();
  const [data, setData] = useState(null);
  const [jobs, setJobs] = useState(null);
  const [status, setStatus] = useState('loading');

  const load = async () => {
    setStatus('loading');
    try {
      const analytics = await api.get('/admin/analytics');
      setData(analytics);
      if (hasRole('super_admin')) {
        api.get('/admin/jobs').then(setJobs).catch(() => setJobs([]));
      }
      setStatus('ready');
    } catch {
      setStatus('error');
    }
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (status === 'loading') {
    return (
      <div className={styles.page}>
        <div className={styles.kpis}>{Array.from({ length: 4 }).map((_, i) => <div key={i} className={styles.skel} />)}</div>
        <div className={styles.skelWide} />
      </div>
    );
  }
  if (status === 'error') {
    return <div className={styles.page}><p className={styles.error}>Couldn’t load analytics. <button onClick={load}>Retry</button></p></div>;
  }

  const funnel = STATUS_ORDER.filter((s) => data.pipeline[s]).map((s) => ({ status: s.replace('_', ' '), count: data.pipeline[s] }));
  const cat = data.categorySplit.map((c) => ({ name: c.category, value: c.count, amount: c.amount }));

  return (
    <div className={styles.page}>
      <header className={styles.head}>
        <div>
          <p className="eyebrow">Overview</p>
          <h1 className={styles.h1}>Operations dashboard</h1>
        </div>
        <button className={styles.refresh} onClick={load}><RefreshCw size={15} /> Refresh</button>
      </header>

      <section className={styles.kpis}>
        <StatCard label="Open cases" value={data.kpis.openCases} icon={FolderKanban} />
        <StatCard label="Deployed" value={formatINR(data.kpis.deployed)} icon={Wallet} />
        <StatCard label="Donations" value={formatINR(data.kpis.donations)} icon={HeartHandshake} />
        <StatCard label="Disbursements" value={data.kpis.disbursements} icon={Users} />
      </section>

      <section className={styles.row2}>
        <Card>
          <CardHeader title="Case pipeline" subtitle="Cases by stage" />
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={funnel} layout="vertical" margin={{ left: 20 }}>
              <XAxis type="number" tick={{ fontSize: 11 }} />
              <YAxis type="category" dataKey="status" tick={{ fontSize: 11, textTransform: 'capitalize' }} width={92} />
              <Tooltip cursor={{ fill: 'rgba(0,0,0,0.04)' }} />
              <Bar dataKey="count" fill="#2D6A4F" radius={[0, 6, 6, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>
        <Card>
          <CardHeader title="Closed cases by category" subtitle="Share of served work" />
          <ResponsiveContainer width="100%" height={240}>
            <PieChart>
              <Pie data={cat} dataKey="value" nameKey="name" innerRadius={52} outerRadius={88} paddingAngle={2}>
                {cat.map((d) => <Cell key={d.name} fill={CAT_COLORS[d.name] || '#888'} />)}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </Card>
      </section>

      <section className={styles.row2}>
        <Card>
          <CardHeader title="Disbursements over time" subtitle="Monthly deployed amount" />
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={data.disbursementsByMonth}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
              <XAxis dataKey="label" tick={{ fontSize: 11 }} />
              <YAxis tickFormatter={(v) => `₹${(v / 1000).toFixed(0)}k`} tick={{ fontSize: 11 }} width={48} />
              <Tooltip formatter={(v) => formatINR(v)} />
              <Line type="monotone" dataKey="amount" stroke="#BF9447" strokeWidth={2.5} dot={{ r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
        </Card>
        <Card>
          <CardHeader title="Recent activity" subtitle="From the audit trail" action={<Activity size={16} />} />
          <ul className={styles.feed}>
            {data.recentActivity.slice(0, 8).map((a) => (
              <li key={a.id || a.seq}>
                <span className={styles.feedDot} />
                <div>
                  <code className={styles.feedAction}>{a.action}</code>
                  <span className={styles.feedMeta}>{a.actor?.name || 'system'} · {formatDate(a.at)}</span>
                </div>
              </li>
            ))}
            {data.recentActivity.length === 0 && <p className={styles.muted}>No recent activity.</p>}
          </ul>
        </Card>
      </section>

      {jobs && (
        <Card>
          <CardHeader title="Background jobs" subtitle="Scheduled job health" action={<Cpu size={16} />} />
          <div className={styles.jobs}>
            {jobs.map((j) => (
              <div key={j.name} className={styles.job}>
                <span className={`${styles.jobDot} ${styles[j.lastStatus] || ''}`} />
                <div>
                  <strong>{j.name}</strong>
                  <span className={styles.muted}>{j.description}</span>
                </div>
                <span className={styles.jobMeta}>
                  {j.lastRunAt ? `ran ${formatDate(j.lastRunAt)}` : 'not yet run'} · {j.schedule}
                </span>
              </div>
            ))}
            {jobs.length === 0 && <p className={styles.muted}>No jobs registered.</p>}
          </div>
        </Card>
      )}
    </div>
  );
}
